import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Card, Center, Stack, Text, VisuallyHidden } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { IconBrandWindows, IconVolume, IconVolumeOff } from '@tabler/icons-react';

import { apiScopes, graphFilesScopes, graphMailScopes } from '../auth/msalConfig';

const INTRO_VIDEO = '/dzik-intro.mp4';
const INTRO_POSTER = '/dzik-intro-poster.jpg';

export function LoginScreen() {
  const { instance } = useMsal();
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduceMotion = useReducedMotion();
  const [soundOn, setSoundOn] = useState(false);

  // Autoodtwarzanie: najpierw próba z głosem, bo o głos chodzi. Przeglądarki
  // blokują dźwięk, dopóki użytkownik nie wejdzie w interakcję z witryną, więc
  // po odmowie wracamy do wersji wyciszonej — animacja musi ruszyć w każdym
  // wypadku, a od dźwięku jest przycisk. Po jednym włączeniu dźwięku Chrome
  // zwykle przepuszcza go przy kolejnych wejściach (media engagement index).
  //
  // `prefers-reduced-motion` respektujemy wprost: wtedy zostaje sam kadr
  // (poster) i decyzja użytkownika.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;

    video.muted = false;
    video.play().then(
      () => setSoundOn(true),
      () => {
        video.muted = true;
        setSoundOn(false);
        void video.play();
      },
    );
  }, [reduceMotion]);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;

    if (soundOn) {
      video.muted = true;
      setSoundOn(false);
      return;
    }
    video.muted = false;
    setSoundOn(true);
    // Od początku, a nie od miejsca, w którym akurat jest: głos ma sens tylko
    // w całości, a klip trwa 10 sekund.
    video.currentTime = 0;
    void video.play();
  };

  // `mih`, nie `h`: karta z animacją jest wysoka, a przy niskim oknie
  // `h="100vh"` obcięłoby ją bez możliwości przewinięcia.
  return (
    <Center mih="100vh" bg="gray.0" px="md" py="xl">
      <Card shadow="md" radius="md" padding="xl" w={760} maw="100%" withBorder>
        <Stack gap="lg" align="center">
          {/* Animacja zastępuje nazwę i podtytuł wypisane tekstem, więc obie
              informacje muszą zostać dla czytników ekranu i dla sytuacji, gdy
              plik się nie wczyta. */}
          <VisuallyHidden>
            <h1>DZiK — Do Zestawień i Kalkulacji. Raporty sprzedaży.</h1>
          </VisuallyHidden>
          <video
            ref={videoRef}
            src={INTRO_VIDEO}
            poster={INTRO_POSTER}
            playsInline
            preload="auto"
            // Bez `loop`: klip jest intrem i kończy się na znaku firmowym —
            // powrót do pierwszego kadru wyglądałby jak zacięcie.
            aria-label="Animacja z maskotką DZiK i znakiem „Do Zestawień i Kalkulacji”"
            style={{
              width: '100%',
              // Sufit wysokości na niskich oknach — wtedy klip skaluje się
              // w dół razem z proporcją, a nie wypycha przycisku logowania
              // poza ekran.
              maxHeight: '52vh',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 'var(--mantine-radius-md)',
              backgroundColor: 'var(--mantine-color-dark-8)',
            }}
          />
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={soundOn ? <IconVolume size={16} /> : <IconVolumeOff size={16} />}
            onClick={toggleSound}
          >
            {soundOn ? 'Wycisz' : 'Włącz dźwięk'}
          </Button>
          <Text size="sm" ta="center">
            Zaloguj się, aby uzyskać dostęp do aplikacji.
          </Text>
          <Button
            fullWidth
            leftSection={<IconBrandWindows size={18} />}
            // Zgoda na pocztę i na pliki doproszona razem z logowaniem — inaczej
            // okno zgody wyskakiwałoby przy pierwszym drafcie i przy pierwszym
            // zapisie na SharePoint, już w trakcie pracy.
            onClick={() =>
              void instance.loginRedirect({
                scopes: [...apiScopes, ...graphMailScopes, ...graphFilesScopes],
              })
            }
          >
            Zaloguj się przez Microsoft
          </Button>
        </Stack>
      </Card>
    </Center>
  );
}
